/**
 * Processing Modal Utilities
 * Centralized functions for managing ProcessingModal state and actions
 */

import { loadWorkflowLastJobId, clearWorkflowLastJobId } from './workflowSessionStorage';
import { updateWorkflowSessionState } from '../store/reducers';
import { apiFetch } from './api';
import { getQueueNavigationInfo, navigateToNextWorkflow } from './modalNavigationUtils';

/**
 * Get the store instance
 * @returns {Object} Redux store instance
 */
const getStore = () => {
  try {
    // Import store directly (works for non-React contexts)
    const storeModule = require('../store/store');
    return storeModule.default || storeModule;
  } catch (e) {
    console.error('ProcessingModalUtils: Could not get store instance.', e);
    throw new Error('Store instance not available');
  }
};

/**
 * Get workflow processing state
 * @param {string} workflowSessionId - The workflow session ID
 * @returns {Object} { isProcessing: boolean, hasJobId: boolean, isInQueue: boolean, isWaiting: boolean, isCompleted: boolean }
 */
export const getWorkflowProcessingState = (workflowSessionId) => {
  try {
    const store = getStore();
    const state = store.getState();
    const workflowSessions = state?.workflowSessions || {};
    const sessions = workflowSessions?.sessions || {};
    const queue = workflowSessions?.processingQueue || [];
    const currentlyProcessingId = workflowSessions?.currentlyProcessingSessionId;
    
    const session = sessions[workflowSessionId];
    if (!session) {
      return {
        isProcessing: false,
        hasJobId: false,
        isInQueue: false,
        isWaiting: false,
        isCompleted: false,
      };
    }
    
    const isProcessing = session?.state?.isProcessing || false;
    const hasJobId = !!loadWorkflowLastJobId(workflowSessionId);
    const isInQueue = queue.includes(workflowSessionId);
    const isCurrentlyProcessing = currentlyProcessingId === workflowSessionId;
    const isCompleted = session?.state?.stepsCompleted || false;
    
    // Workflow is processing if: isProcessing flag is true, hasJobId, or isCurrentlyProcessing
    const isActuallyProcessing = isProcessing || hasJobId || isCurrentlyProcessing;
    
    // Backend supports multiple concurrent jobs - all workflows can process simultaneously
    const isWaiting = false;
    
    return {
      isProcessing: isActuallyProcessing, // Return combined processing state
      hasJobId,
      isInQueue,
      isWaiting,
      isCompleted,
    };
  } catch (error) {
    console.error('Error getting workflow processing state:', error);
    return {
      isProcessing: false,
      hasJobId: false,
      isInQueue: false,
      isWaiting: false,
      isCompleted: false,
    };
  }
};

/**
 * Check if any ProcessingModal is currently open (any workflow is processing or has a jobId)
 * CRITICAL: Exclude completed workflows - they should not block the template modal
 * @returns {boolean} True if any workflow is processing or has a jobId (but NOT if completed)
 */
export const isAnyProcessingModalOpen = () => {
  try {
    const store = getStore();
    const state = store.getState();
    const workflowSessions = state?.workflowSessions || {};
    const sessions = workflowSessions?.sessions || {};
    
    // Check if any workflow is processing or has a jobId (exclude completed workflows)
    const workflowStates = Object.keys(sessions).map(sessionId => {
      const session = sessions[sessionId];
      const isProcessing = session?.state?.isProcessing || false;
      const hasJobId = !!loadWorkflowLastJobId(sessionId);
      const isCompleted = session?.state?.stepsCompleted || false;
      const isOpen = (isProcessing || hasJobId) && !isCompleted;
      
      return {
        sessionId,
        isProcessing,
        hasJobId,
        isCompleted,
        isOpen,
      };
    });
    
    const anyProcessing = workflowStates.some(w => w.isOpen);
    
    // Note: Completed workflows with jobId are correctly excluded from the "open" check The jobId is kept for history/tracking purposes but doesn't block the template modal
    
    return anyProcessing;
  } catch (error) {
    console.error('Error checking if ProcessingModal is open:', error);
    return false;
  }
};

/**
 * Reset processing states to initial values
 */
export const resetProcessingStates = ({
  setHasError,
  setErrorMessage,
  setCurrentStep,
  setCompletedSteps,
  setAreStepsCompleted,
  setProcessingStatus,
  setIsThinking,
  setIsApiCallInProgress,
  dispatch,
  errorHandledRef,
}) => {
  errorHandledRef.current = false;
  setHasError(false);
  setErrorMessage("");
  setCurrentStep(0);
  setCompletedSteps([]);
  setAreStepsCompleted(false);
  setProcessingStatus("");
  setIsThinking(false);
  dispatch({ type: 'SET_STEPS_COMPLETED', payload: false });
  // setIsApiCallInProgress(true); // Removed this as it's often set by the caller
};

/**
 * Reset modal state when workflow changes
 * CRITICAL: Don't clear modalClosed here - it should persist per workflow
 * Only clear it when processing starts (resetModalStateOnProcessingStart)
 */
export const resetModalStateOnWorkflowChange = ({
  setHasError, setErrorMessage, setCurrentStep, setCompletedSteps,
  setAreStepsCompleted, setProcessingStatus, setIsThinking, setJobId,
  setIsMinimized, dispatch, errorHandledRef, processingTriggeredRef,
  hasDispatchedQueueEventRef, userMaximizedInactiveRef, initialJobId,
  forceMinimized,
}) => {
  resetProcessingStates({
    setHasError, setErrorMessage, setCurrentStep, setCompletedSteps,
    setAreStepsCompleted, setProcessingStatus, setIsThinking,
    setIsApiCallInProgress: () => {}, // Don't reset this here
    dispatch, errorHandledRef,
  });

  processingTriggeredRef.current = false;
  hasDispatchedQueueEventRef.current = false;
  userMaximizedInactiveRef.current = false;

  if (initialJobId) {
    setJobId(initialJobId);
  } else {
    setJobId(null);
  }

  setIsMinimized(forceMinimized);
};

/**
 * Reset modal state when processing starts
 * CRITICAL: Clear modalClosed flag when processing starts so modal can show again
 */
export const resetModalStateOnProcessingStart = ({
  setHasError, setErrorMessage, setCurrentStep, setCompletedSteps,
  setAreStepsCompleted, setProcessingStatus, setIsThinking, setJobId,
  setIsMinimized, dispatch, errorHandledRef, processingTriggeredRef,
  hasDispatchedQueueEventRef, userMaximizedInactiveRef, initialJobId,
  forceMinimized,
  modalWorkflowSessionId,
}) => {
  resetProcessingStates({
    setHasError, setErrorMessage, setCurrentStep, setCompletedSteps,
    setAreStepsCompleted, setProcessingStatus, setIsThinking,
    setIsApiCallInProgress: () => {}, // Don't reset this here
    dispatch, errorHandledRef,
  });

  // Safely reset refs if they exist
  if (processingTriggeredRef) {
    processingTriggeredRef.current = false;
  }
  if (hasDispatchedQueueEventRef) {
    hasDispatchedQueueEventRef.current = false;
  }
  if (userMaximizedInactiveRef) {
    userMaximizedInactiveRef.current = false;
  }

  // CRITICAL: Clear modalClosed flag when processing starts so modal can show again
  if (modalWorkflowSessionId && dispatch) {
    dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
      modalClosed: false,
    }));
  }

  if (initialJobId) {
    setJobId(initialJobId);
  } else {
    setJobId(null);
  }

  setIsMinimized(forceMinimized);
};

/**
 * Get workflow completion state from Redux
 * @param {string} workflowSessionId - The workflow session ID
 * @returns {boolean} True if workflow steps are completed
 */
export const getWorkflowStepsCompleted = (workflowSessionId) => {
  try {
    const store = getStore();
    const state = store.getState();
    const workflowSessions = state?.workflowSessions || {};
    const session = workflowSessions?.sessions?.[workflowSessionId];
    return session?.state?.stepsCompleted || false;
  } catch (error) {
    console.error('Error getting workflow steps completed:', error);
    return false;
  }
};

/**
 * Set workflow completion state in Redux
 * @param {string} workflowSessionId - The workflow session ID
 * @param {boolean} completed - Whether steps are completed
 * @param {Function} dispatch - Redux dispatch function
 */
export const setWorkflowStepsCompleted = (workflowSessionId, completed, dispatch) => {
  if (!workflowSessionId || !dispatch) {
    return;
  }
  
  dispatch(updateWorkflowSessionState(workflowSessionId, {
    stepsCompleted: completed,
  }));
};

/**
 * Initialize modal completion state from workflow session state
 * @param {string} workflowSessionId - The workflow session ID
 * @param {Function} setAreStepsCompleted - State setter for areStepsCompleted
 * @param {Function} setCurrentStep - State setter for currentStep
 * @param {Function} setCompletedSteps - State setter for completedSteps
 * @returns {boolean} True if workflow was already completed
 */
export const initializeModalCompletionState = (
  workflowSessionId,
  setAreStepsCompleted,
  setCurrentStep,
  setCompletedSteps
) => {
  const isCompleted = getWorkflowStepsCompleted(workflowSessionId);
  
  if (isCompleted) {
    setAreStepsCompleted(true);
    setCurrentStep(2);
    setCompletedSteps([0, 1, 2]);
    return true;
  }
  
  return false;
};

/**
 * Handle closing the modal
 * CRITICAL: For completed workflows, clear the completion state when user explicitly closes
 * This allows the modal to actually close (since shouldShow checks areStepsCompleted)
 * Also automatically switches to next workflow in queue if available
 * CRITICAL: Clears jobId from sessionStorage and sets modalClosed flag to prevent modal from showing again
 */
export const handleCloseModal = ({
  jobId,
  onCancelProcessing,
  setAreStepsCompleted,
  setIsProcessing,
  dispatch,
  saveChanges,
  modalWorkflowSessionId,
  workflowWasCompleted,
}) => {
  setAreStepsCompleted(false);
  setIsProcessing(false);

  if (modalWorkflowSessionId) {
    dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
      isProcessing: false,
      stepsCompleted: false,
      modalClosed: true,
    }));
    
    try {
      const store = getStore();
      const state = store.getState();
      const workflowSessions = state?.workflowSessions || {};
      
      const navigationInfo = getQueueNavigationInfo({
        workflowSessionId: modalWorkflowSessionId,
        workflowSessions,
      });
      
      if (navigationInfo.hasNext && navigationInfo.nextWorkflowId) {
        setTimeout(() => {
          navigateToNextWorkflow({
            navigationInfo,
            dispatch,
          });
        }, 100);
      }
    } catch (error) {
      // Silent error handling
    }
  }

  if (onCancelProcessing) {
    onCancelProcessing();
  }

  if (saveChanges) {
    saveChanges();
  }
};

/**
 * Handle canceling processing
 * CRITICAL: Also clear jobId from sessionStorage and set modalClosed flag
 */
export const handleCancelProcessing = async ({
  jobId,
  onCancelProcessing,
  setIsProcessing,
  setCurrentStep,
  setCompletedSteps,
  setAreStepsCompleted,
  setProcessingStatus,
  setJobId,
  dispatch,
  modalWorkflowSessionId,
}) => {
  if (jobId) {
    try {
      await cancelJob({ jobId, setIsProcessing, setJobId });
    } catch (error) {
      // Silent error handling
    }
  }

  setIsProcessing(false);
  setCurrentStep(0);
  setCompletedSteps([]);
  setAreStepsCompleted(false);
  setProcessingStatus("");
  setJobId(null);

  if (modalWorkflowSessionId && dispatch) {
    clearWorkflowLastJobId(modalWorkflowSessionId);
    dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
      isProcessing: false,
      modalClosed: true,
    }));
  }

  if (onCancelProcessing) {
    onCancelProcessing();
  }
};

/**
 * Cancel a job via API
 */
export const cancelJob = async ({ jobId, setIsProcessing, setJobId }) => {
  if (!jobId) {
    return;
  }

  try {
    await apiFetch(`/api/jobs/${jobId}/cancel`, {
      method: 'POST',
    });
    
    setIsProcessing(false);
    setJobId(null);
  } catch (error) {
    throw error;
  }
};

/**
 * Handle stream error - called when stream fails or encounters an error
 * @param {Object} params
 * @param {string} params.workflowSessionId - Workflow session ID
 * @param {Error|string} params.error - Error object or error message
 * @param {Function} params.setHasError - State setter for hasError
 * @param {Function} params.setErrorMessage - State setter for errorMessage
 * @param {Function} params.setIsProcessing - State setter for isProcessing
 * @param {Function} params.setIsMinimized - State setter for isMinimized
 * @param {Function} params.dispatch - Redux dispatch function
 */
export const handleStreamError = ({
  workflowSessionId,
  error,
  setHasError,
  setErrorMessage,
  setIsProcessing,
  setIsMinimized,
  dispatch,
}) => {
  const errorMessage = error?.message || error || 'An error occurred during document processing stream.';
  
  if (process.env.NODE_ENV === 'development') {
    console.error(`❌ [Stream Error] Workflow ${workflowSessionId}:`, {
      error: errorMessage,
      errorObject: error,
    });
  }
  
  setHasError(true);
  setErrorMessage(errorMessage);
  setIsProcessing(false);
  setIsMinimized(false);
  
  if (workflowSessionId && dispatch) {
    dispatch(updateWorkflowSessionState(workflowSessionId, {
      isProcessing: false,
    }));
  }
};

/**
 * Handle job error - called when job API call fails
 * @param {Object} params
 * @param {string} params.workflowSessionId - Workflow session ID
 * @param {Object} params.responseData - API response data
 * @param {number} params.statusCode - HTTP status code
 * @param {Function} params.setHasError - State setter for hasError
 * @param {Function} params.setErrorMessage - State setter for errorMessage
 * @param {Function} params.setIsProcessing - State setter for isProcessing
 * @param {Function} params.setIsMinimized - State setter for isMinimized
 * @param {Function} params.dispatch - Redux dispatch function
 */
export const handleJobError = ({
  workflowSessionId,
  responseData,
  statusCode,
  setHasError,
  setErrorMessage,
  setIsProcessing,
  setIsMinimized,
  dispatch,
}) => {
  const errorMessage = responseData?.message || 
                       responseData?.error || 
                       responseData?.detail || 
                       `HTTP error! status: ${statusCode}`;
  
  if (process.env.NODE_ENV === 'development') {
    console.error(`❌ [Job Error] Workflow ${workflowSessionId}:`, {
      statusCode,
      status: responseData?.status,
      message: responseData?.message,
      error: responseData?.error,
      detail: responseData?.detail,
      code: responseData?.code,
      fullResponse: responseData,
    });
  }
  
  setHasError(true);
  setErrorMessage(errorMessage);
  setIsProcessing(false);
  setIsMinimized(false);
  
  if (workflowSessionId && dispatch) {
    dispatch(updateWorkflowSessionState(workflowSessionId, {
      isProcessing: false,
    }));
  }
};

/**
 * Get workflow data state (documents, extractedData, etc.)
 * @param {Object} sessionState - Workflow session state
 * @returns {Object} { hasDocuments: boolean, hasExtractedData: boolean, hasData: boolean }
 */
export const getWorkflowDataState = (sessionState) => {
  if (!sessionState) {
    return {
      hasDocuments: false,
      hasExtractedData: false,
      hasData: false,
    };
  }
  
  const hasDocuments = !!(sessionState.documents && sessionState.documents.length > 0);
  const hasExtractedData = !!sessionState.extractedData;
  const hasData = hasDocuments || hasExtractedData;
  
  return {
    hasDocuments,
    hasExtractedData,
    hasData,
  };
};

/**
 * Get workflow step state based on completion and processing status
 * @param {Object} params
 * @param {boolean} params.stepsCompleted - Whether workflow steps are completed
 * @param {boolean} params.isActuallyProcessing - Whether workflow is actually processing
 * @param {number} params.initialStep - Initial step if jobId exists (default: 0)
 * @returns {Object} { currentStep: number, completedSteps: number[] }
 */
export const getWorkflowStepState = ({
  stepsCompleted,
  isActuallyProcessing,
  initialStep = 0,
}) => {
  if (stepsCompleted) {
    return {
      currentStep: 2,
      completedSteps: [0, 1, 2],
    };
  }
  
  if (isActuallyProcessing) {
    return {
      currentStep: initialStep > 0 ? initialStep : 1,
      completedSteps: [0],
    };
  }
  
  return {
    currentStep: 0,
    completedSteps: [],
  };
};

/**
 * Determine if a workflow should show a processing modal
 * @param {Object} params
 * @param {string} params.workflowSessionId - Workflow session ID
 * @param {boolean} params.isActiveWorkflow - Whether this is the active workflow
 * @param {Object} params.workflowSessions - Workflow sessions state from Redux
 * @param {boolean} params.isProcessing - Is processing prop (for active workflow)
 * @returns {Object} { shouldShow: boolean, reason: string }
 */
export const shouldShowProcessingModal = ({
  workflowSessionId,
  isActiveWorkflow,
  workflowSessions,
  isProcessing = false,
}) => {
  if (!workflowSessionId || !workflowSessions) {
    return { shouldShow: false, reason: 'missing_params' };
  }
  
  const sessions = workflowSessions?.sessions || {};
  const session = sessions[workflowSessionId];
  if (!session) {
    return { shouldShow: false, reason: 'no_session' };
  }
  
  const sessionState = session.state || {};
  const modalClosed = sessionState.modalClosed || false;
  const stepsCompleted = sessionState.stepsCompleted || false;
  const workflowIsProcessing = sessionState.isProcessing || false;
  
  // Don't show if modal was closed by user
  if (modalClosed) {
    return { shouldShow: false, reason: 'modal_closed' };
  }
  
  // Don't show if completed (completed workflows should not show processing modal)
  if (stepsCompleted) {
    return { shouldShow: false, reason: 'completed' };
  }
  
  // Get data state
  const dataState = getWorkflowDataState(sessionState);
  
  // Get processing state
  const processingState = getWorkflowProcessingState(workflowSessionId);
  const hasJobId = processingState.hasJobId;
  const isInQueue = processingState.isInQueue;
  
  // Determine if workflow is actually processing
  const isActuallyProcessing = isActiveWorkflow 
    ? (isProcessing || workflowIsProcessing || hasJobId)
    : (workflowIsProcessing || hasJobId || processingState.isProcessing);
  
  // For active workflows: show if processing OR has jobId OR is in queue For inactive workflows: show if processing OR has jobId OR is in queue
  const shouldShow = isActuallyProcessing || isInQueue;
  
  // Must have data to show
  if (!shouldShow || !dataState.hasData) {
    return { 
      shouldShow: false, 
      reason: shouldShow ? 'no_data' : 'not_processing' 
    };
  }
  
  return { shouldShow: true, reason: 'processing_or_queued' };
};

/**
 * Get comprehensive workflow state for rendering
 * Combines processing state, data state, and step state
 * @param {Object} params
 * @param {string} params.workflowSessionId - Workflow session ID
 * @param {boolean} params.isActiveWorkflow - Whether this is the active workflow
 * @param {Object} params.workflowSessions - Workflow sessions state from Redux
 * @param {boolean} params.isProcessing - Is processing prop (for active workflow)
 * @param {string} params.initialJobId - Initial jobId if exists
 * @returns {Object} Comprehensive workflow state object
 */
export const getComprehensiveWorkflowState = ({
  workflowSessionId,
  isActiveWorkflow,
  workflowSessions,
  isProcessing = false,
  initialJobId = null,
}) => {
  if (!workflowSessionId || !workflowSessions) {
    return null;
  }
  
  const sessions = workflowSessions?.sessions || {};
  const session = sessions[workflowSessionId];
  if (!session) {
    return null;
  }
  
  const sessionState = session.state || {};
  const workflowName = session.name || `Workflow ${workflowSessionId.replace('workflow-', '')}`;
  
  // Get processing state
  const processingState = getWorkflowProcessingState(workflowSessionId);
  
  // Get data state
  const dataState = getWorkflowDataState(sessionState);
  
  // Determine if workflow is actually processing
  const workflowIsProcessing = sessionState.isProcessing || false;
  const isActuallyProcessing = isActiveWorkflow
    ? (isProcessing || workflowIsProcessing || processingState.hasJobId || !!initialJobId)
    : (workflowIsProcessing || processingState.hasJobId);
  
  // Get step state
  const stepState = getWorkflowStepState({
    stepsCompleted: sessionState.stepsCompleted || false,
    isActuallyProcessing,
    initialStep: initialJobId ? 1 : 0,
  });
  
  // Get queue navigation info
  const queueNavigationInfo = getQueueNavigationInfo({
    workflowSessionId,
    workflowSessions,
  });
  
  // Determine waiting state (currently always false with simultaneous processing)
  const isWaiting = false;
  
  // Determine if should show
  const visibility = shouldShowProcessingModal({
    workflowSessionId,
    isActiveWorkflow,
    workflowSessions,
    isProcessing,
  });
  
  return {
    // Session info
    workflowSessionId,
    workflowName,
    sessionState,
    
    // Processing state
    ...processingState,
    isActuallyProcessing,
    workflowIsProcessing,
    
    // Data state
    ...dataState,
    
    // Step state
    ...stepState,
    
    // Queue info
    queuePosition: queueNavigationInfo.position,
    totalInQueue: queueNavigationInfo.total,
    
    // Waiting state
    isWaiting,
    
    // Visibility
    shouldShow: visibility.shouldShow,
    visibilityReason: visibility.reason,
    
    // Other state
    modalClosed: sessionState.modalClosed || false,
    stepsCompleted: sessionState.stepsCompleted || false,
    modalMinimized: sessionState.modalMinimized || false,
  };
};
